/*
 This file is part of GNU Taler
 (C) 2019-2020 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 * @fileoverview
 * Construction and parsing of taler:// URIs.
 * Specification: https://lsd.gnunet.org/lsd0006/
 */

/**
 * Imports.
 */
import { Amounts } from "./amounts.js";
import { Codec, Context, DecodingError, renderContext } from "./codec.js";
import { assertUnreachable } from "./errors.js";
import { HostPortPath, Paytos } from "./payto.js";
import { Result, ResultError, ResultOk } from "./result.js";
import { AmountString, HashCodeString } from "./types-taler-common.js";
import { URL, URLSearchParams } from "./url.js";

export enum TalerUriAction {
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.1
   */
  Withdraw = "withdraw",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.2
   */
  Pay = "pay",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.3
   */
  Refund = "refund",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.4
   */
  PayPush = "pay-push",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.5
   */
  PayPull = "pay-pull",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.6
   */
  PayTemplate = "pay-template",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.7
   */
  Restore = "restore",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.8
   */
  DevExperiment = "dev-experiment",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.9
   */
  AddExchange = "add-exchange",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.10
   */
  WithdrawExchange = "withdraw-exchange",
  /**
   * https://lsd.gnunet.org/lsd0006/#section-5.12
   */
  WithdrawalTransferResult = "withdrawal-transfer-result",
  /**
   * FIXME: LSD
   * Add a contact to the wallet
   */
  AddContact = "add-contact",
}

/**
 * A parsed taler URI.
 */
export type TalerUri =
  | TalerPayUriResult
  | TalerPayTemplateUri
  | TalerDevExperimentUri
  | TalerPayPullUri
  | TalerPayPushUri
  | TalerRestoreUri
  | TalerRefundUri
  | TalerWithdrawUri
  | TalerWithdrawExchangeUri
  | TalerAddExchangeUri
  | TalerWithdrawalTransferResultUri
  | TalerAddContactUri;

declare const __action_str: unique symbol;
export type TalerUriString = string & { [__action_str]: true };

export function codecForTalerUriString(): Codec<TalerUriString> {
  return {
    decode(x: any, c?: Context): TalerUriString {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (Result.isError(TalerUris.parse(x))) {
        throw new DecodingError(
          `invalid taler URI at ${renderContext(c)} but got "${x}"`,
        );
      }
      return x as TalerUriString;
    },
  };
}

const TALER_PREFIX = "taler://";
const TALER_HTTP_PREFIX = "taler+http://";

export enum TalerUriParseError {
  /**
   * URI should start with taler:// or taler+http://
   */
  WRONG_PREFIX,
  /**
   * URI should have a / after the target type
   */
  INCOMPLETE,
  /**
   * URI type is not in the list of supported types
   */
  UNSUPPORTED,
  /**
   * The quantity of components is wrong based on the target type
   */
  COMPONENTS_LENGTH,
  /**
   * The validation of one path component failed
   */
  INVALID_TARGET_PATH,
  /**
   * The validation of one parameter component failed
   */
  INVALID_PARAMETER,
  /**
   * The taler action is unsupported.
   */
  UNEXPECTED_ACTION,
}

/**
 * A merchant base URL ends in the instance segment, e.g.
 * "https://shop.example.com/instances/myshop/".
 */
const MERCHANT_INSTANCE_SEGMENT_RE = /\/instances\/([^/]+)\/?$/;

/**
 * Fold the instance segment of a merchant base URL to lower case.
 *
 * Merchant instance IDs are case-insensitive: the backend folds them when the
 * instance is created and again on every lookup, and only ever hands out the
 * lower-case spelling itself.  So a base URL that a wallet derived from a
 * taler:// URI can name the same instance as the one in the contract terms
 * and still differ as a string, which matters wherever the base URL is used
 * as an identity rather than just as a request target.
 *
 * Only the instance segment is folded.  The host is already case-insensitive
 * per RFC 3986, but any leading path segments can come from a reverse-proxy
 * prefix, where case is significant.
 */
export function canonicalizeMerchantInstanceUrl(baseUrl: string): string {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    // Not our problem here; whoever consumes the URL will report it.
    return baseUrl;
  }
  const match = MERCHANT_INSTANCE_SEGMENT_RE.exec(url.pathname);
  if (!match) {
    return baseUrl;
  }
  // Instance IDs are slugs (ASCII alphanumerics plus "-_.:"), so a plain
  // toLowerCase matches the backend's GNUNET_STRINGS_utf8_tolower.
  const canonicalId = match[1].toLowerCase();
  if (canonicalId === match[1]) {
    return baseUrl;
  }
  const trailingSlash = url.pathname.endsWith("/") ? "/" : "";
  url.pathname =
    url.pathname.slice(0, match.index) +
    `/instances/${canonicalId}` +
    trailingSlash;
  return url.href;
}

export namespace TalerUris {
  export type URI = TalerUri;

  const supportedTargets: Record<TalerUriAction, true> = {
    "add-contact": true,
    "add-exchange": true,
    "dev-experiment": true,
    pay: true,
    "pay-pull": true,
    "pay-template": true,
    "pay-push": true,
    "withdraw-exchange": true,
    refund: true,
    restore: true,
    withdraw: true,
    "withdrawal-transfer-result": true,
  };

  function asHost(s: HostPortPath): string {
    const b = new URL(s);
    return `${b.host}${b.pathname}`;
  }

  function getTalerParamList(p: URI): [string, string][] {
    const result: [string, string][] = [];

    switch (p.type) {
      case TalerUriAction.Withdraw: {
        if (p.externalConfirmation) {
          result.push(["external-confirmation", "1"]);
        }
        return result;
      }
      case TalerUriAction.Pay: {
        if (p.claimToken) {
          result.push(["c", p.claimToken]);
        }
        if (p.noncePriv) {
          result.push(["n", p.noncePriv]);
        }
        return result;
      }
      case TalerUriAction.WithdrawExchange: {
        if (p.amount) {
          result.push(["a", p.amount]);
        }
        return result;
      }
      case TalerUriAction.WithdrawalTransferResult: {
        result.push(["ref", p.ref]);
        if (p.status) {
          result.push(["status", p.status]);
        }
        return result;
      }
      case TalerUriAction.AddContact: {
        if (p.sourceBaseUrl) {
          result.push(["sourceBaseUrl", p.sourceBaseUrl]);
        }
        return result;
      }
      case TalerUriAction.PayTemplate: {
        if (p.fulfillmentUrl) {
          result.push(["fulfillment_url", p.fulfillmentUrl]);
        }
        if (p.sessionId) {
          result.push(["session_id", p.sessionId]);
        }
        if (p.amount) {
          result.push(["amount", p.amount]);
        }
        if (p.summary) {
          result.push(["summary", p.summary]);
        }
        if (p.editableAmount) {
          result.push(["editable_amount", "1"]);
        }
        if (p.editableSummary) {
          result.push(["editable_summary", "1"]);
        }
        return result;
      }
      case TalerUriAction.DevExperiment: {
        Object.entries(p.query ?? {}).forEach(([key, value]) => {
          result.push([key, value]);
        });
        return result;
      }
      case TalerUriAction.Refund:
      case TalerUriAction.PayPush:
      case TalerUriAction.PayPull:
      case TalerUriAction.Restore:
      case TalerUriAction.AddExchange: {
        return result;
      }
      default: {
        assertUnreachable(p);
      }
    }
  }

  function getTalerPrefix(p: URI): string {
    switch (p.type) {
      case TalerUriAction.Withdraw:
        return p.bankIntegrationApiBaseUrl.startsWith("http://")
          ? TALER_HTTP_PREFIX
          : TALER_PREFIX;
      case TalerUriAction.Pay:
      case TalerUriAction.Refund:
      case TalerUriAction.PayTemplate:
        return p.merchantBaseUrl.startsWith("http://")
          ? TALER_HTTP_PREFIX
          : TALER_PREFIX;
      case TalerUriAction.PayPush:
      case TalerUriAction.PayPull:
      case TalerUriAction.AddExchange:
      case TalerUriAction.WithdrawExchange:
        return p.exchangeBaseUrl.startsWith("http://")
          ? TALER_HTTP_PREFIX
          : TALER_PREFIX;
      case TalerUriAction.Restore:
      case TalerUriAction.DevExperiment:
      case TalerUriAction.WithdrawalTransferResult:
      case TalerUriAction.AddContact:
        return TALER_PREFIX;
      default:
        assertUnreachable(p);
    }
  }

  function getTalerPath(p: URI): string {
    /**
     * After the host we should not add a / since the href
     * already adds one
     */
    switch (p.type) {
      case TalerUriAction.Withdraw:
        return `/${asHost(p.bankIntegrationApiBaseUrl)}${encodeUriSegment(
          p.withdrawalOperationId,
        )}`;
      case TalerUriAction.Pay:
        // The session ID is the last path component of the URI and thus
        // cannot be omitted; an empty component means "no session".
        return `/${asHost(p.merchantBaseUrl)}${encodeUriSegment(
          p.orderId,
        )}/${encodeUriSegment(p.sessionId ?? "")}`;
      case TalerUriAction.Refund:
        // refund should end with a /
        return `/${asHost(p.merchantBaseUrl)}${encodeUriSegment(p.orderId)}/`;
      case TalerUriAction.PayTemplate:
        return `/${asHost(p.merchantBaseUrl)}${encodeUriSegment(p.templateId)}`;
      case TalerUriAction.PayPush:
        return `/${asHost(p.exchangeBaseUrl)}${encodeUriSegment(p.contractPriv)}`;
      case TalerUriAction.PayPull:
        return `/${asHost(p.exchangeBaseUrl)}${encodeUriSegment(p.contractPriv)}`;
      case TalerUriAction.AddExchange:
        return `/${asHost(p.exchangeBaseUrl)}`;
      case TalerUriAction.WithdrawExchange:
        return `/${asHost(p.exchangeBaseUrl)}`;
      case TalerUriAction.Restore:
        return `/${encodeUriSegment(p.walletRootPriv)}/${p.providers
          .map((d) => encodeURIComponent(d))
          .join(",")}`;
      case TalerUriAction.DevExperiment:
        return `/${encodeUriSegment(p.devExperimentId)}`;
      case TalerUriAction.WithdrawalTransferResult:
        // No path component; the data is carried in query parameters.
        return ``;
      case TalerUriAction.AddContact:
        return `/${encodeUriSegment(p.aliasType)}/${encodeUriSegment(
          p.alias,
        )}/${asHost(p.mailboxBaseUri as HostPortPath)}/${encodeUriSegment(
          p.mailboxIdentity,
        )}`;
      default:
        assertUnreachable(p);
    }
  }

  export function stringify(p: URI): TalerUriString {
    const prefix = getTalerPrefix(p);
    const path = getTalerPath(p);
    const paramList = getTalerParamList(p);
    const url = new URL(`${prefix}${p.type}${path}`);
    url.search = createSearchParams(paramList);
    return url.href as TalerUriString;
  }

  export interface PaytoParseOptions {
    /**
     * do not check path component format
     */
    ignoreComponentError?: boolean;
  }

  export type InvalidTargetPathDetail =
    | {
        uriType: TalerUriAction.Restore;
      }
    | {
        uriType: TalerUriAction.Pay;
      }
    | {
        uriType: TalerUriAction.Withdraw;
      }
    | {
        uriType: TalerUriAction.Refund;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.Refund;
        pos: 1;
      }
    | {
        uriType: TalerUriAction.PayPull;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.PayPush;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.PayTemplate;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.WithdrawExchange;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.WithdrawExchange;
        pos: 1;
      }
    | {
        uriType: TalerUriAction.AddExchange;
        pos: 0;
      }
    | {
        uriType: TalerUriAction.AddContact;
        pos: 0;
      };

  export type ParseResult =
    | ResultOk<TalerUri>
    | ResultError<TalerUriParseError.WRONG_PREFIX>
    | ResultError<TalerUriParseError.UNSUPPORTED, { uriType: string }>
    | ResultError<TalerUriParseError.INCOMPLETE, { uriType: string }>
    | ResultError<
        TalerUriParseError.COMPONENTS_LENGTH,
        { uriType: TalerUriAction }
      >
    | ResultError<
        TalerUriParseError.INVALID_TARGET_PATH,
        InvalidTargetPathDetail
      >
    | ResultError<
        TalerUriParseError.INVALID_PARAMETER,
        { uriType: TalerUriAction; name: string }
      >
    | ResultError<TalerUriParseError.UNEXPECTED_ACTION>;

  /**
   * Parse a taler:// URI.
   */
  export function parse(s: string, opts: PaytoParseOptions = {}): ParseResult {
    // check prefix
    let isHttp = false;
    const prefixCheckStr = s.toLowerCase();
    if (
      !prefixCheckStr.startsWith(TALER_PREFIX) &&
      !(isHttp = prefixCheckStr.startsWith(TALER_HTTP_PREFIX))
    ) {
      return Result.error(TalerUriParseError.WRONG_PREFIX);
    }
    const scheme = isHttp ? ("http" as const) : ("https" as const);

    // get path and search
    // RFC 3986 section 3.4: "?" is legal inside the query.
    const rest = s.slice((isHttp ? TALER_HTTP_PREFIX : TALER_PREFIX).length);
    const queryPos = rest.indexOf("?");
    const path = queryPos === -1 ? rest : rest.slice(0, queryPos);
    const search = queryPos === -1 ? undefined : rest.slice(queryPos + 1);

    // check if supported
    const firstSlashPos = path.indexOf("/");
    const uriTypeUncased = (
      firstSlashPos === -1 ? path : path.slice(0, firstSlashPos)
    ) as TalerUriAction;

    const uriType = uriTypeUncased.toLowerCase() as TalerUriAction;

    if (!Object.prototype.hasOwnProperty.call(supportedTargets, uriType)) {
      return Result.errorWithDetail(TalerUriParseError.UNSUPPORTED, {
        uriType,
      });
    }

    const targetPath =
      firstSlashPos === -1 ? "" : path.slice(firstSlashPos + 1);
    // withdrawal-transfer-result carries all its data in query parameters and
    // so legitimately has no path.
    if (
      uriType !== TalerUriAction.WithdrawalTransferResult &&
      (firstSlashPos === -1 || !targetPath)
    ) {
      return Result.errorWithDetail(TalerUriParseError.INCOMPLETE, {
        uriType,
      });
    }

    // parse params
    const params: { [k: string]: string } = {};
    if (search) {
      const searchParams = new URLSearchParams(search);
      searchParams.forEach((v, k) => {
        // URLSearchParams already decodes uri components
        params[k] = v;
      });
    }

    // get URI components
    const cs = targetPath.split("/");
    switch (uriType) {
      case TalerUriAction.Pay:
        return parsePay(scheme, uriType, cs, params, opts);
      case TalerUriAction.Withdraw:
        return parseWithdraw(scheme, uriType, cs, params, opts);
      case TalerUriAction.Refund:
        return parseRefund(scheme, uriType, cs, params, opts);
      case TalerUriAction.PayPull:
        return parsePayPull(scheme, uriType, cs, params, opts);
      case TalerUriAction.PayPush:
        return parsePayPush(scheme, uriType, cs, params, opts);
      case TalerUriAction.PayTemplate:
        return parsePayTemplate(scheme, uriType, cs, params, opts);
      case TalerUriAction.Restore:
        return parseRestore(scheme, uriType, cs, params, opts);
      case TalerUriAction.DevExperiment:
        return parseDevExperiment(scheme, uriType, cs, params, opts);
      case TalerUriAction.WithdrawExchange:
        return parseWithdrawExchange(scheme, uriType, cs, params, opts);
      case TalerUriAction.AddExchange:
        return parseAddExchange(scheme, uriType, cs, params, opts);
      case TalerUriAction.WithdrawalTransferResult:
        return parseWithdrawalTransferResult(scheme, uriType, cs, params);
      case TalerUriAction.AddContact:
        return parseAddContact(scheme, uriType, cs, params);
      default: {
        assertUnreachable(uriType);
      }
    }
  }

  type ValueOf<T> = T[keyof T];
  type TalerUriTyped<T extends TalerUriAction> = ValueOf<{
    [K in TalerUri as K["type"] extends T ? K["type"] : never]: K;
  }>;

  export function parseRestricted<T extends TalerUriAction>(
    s: string,
    t: T,
  ): Result<TalerUriTyped<T>, TalerUriParseError> {
    const p = parse(s);
    if (Result.isError(p)) {
      return p;
    }
    if (p.value.type !== t) {
      return Result.error(TalerUriParseError.UNEXPECTED_ACTION);
    }
    // TypeScript's type system is too weak to type check this
    // without a cast.
    const pt: TalerUriTyped<T> = p.value as TalerUriTyped<T>;
    return Result.of(pt);
  }
}

function parseWithdraw(
  scheme: "http" | "https",
  uriType: TalerUriAction.Withdraw,
  cs: string[],
  params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 2) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get bank host
  const bank = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !bank) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: bank,
    });
  }

  // get operation id
  const operationId = decodeUriSegment(cs[cs.length - 1]);
  // get external confirmation
  const externalConfirmation = !params["external-confirmation"]
    ? undefined
    : params["external-confirmation"] === "1";

  return Result.of<TalerUri>({
    type: TalerUriAction.Withdraw,
    bankIntegrationApiBaseUrl: bank ?? (cs[0] as HostPortPath),
    withdrawalOperationId: operationId,
    externalConfirmation,
  });
}

function parsePay(
  scheme: "http" | "https",
  uriType: TalerUriAction.Pay,
  cs: string[],
  params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 3) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get merchant host
  const merchant = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -2).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !merchant) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: merchant,
    });
  }

  // get order
  const orderId = decodeUriSegment(cs[cs.length - 2]);
  // get session; the merchant emits an empty last component for orders
  // that are not bound to a session, which is not a session ID
  const sessionId = decodeUriSegment(cs[cs.length - 1]) || undefined;

  let nfc: boolean | undefined;
  if ("nfc" in params) {
    nfc = params["nfc"] === "1";
  }

  return Result.of<TalerUri>({
    type: TalerUriAction.Pay,
    merchantBaseUrl: merchant ?? (cs[0] as HostPortPath),
    orderId,
    sessionId,
    claimToken: params["c"],
    noncePriv: params["n"],
    nfc,
  });
}

function parsePayPush(
  scheme: "http" | "https",
  uriType: TalerUriAction.PayPush,
  cs: string[],
  _params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 2) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get exchange host
  const exchange = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !exchange) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: exchange,
    });
  }

  // get contract priv
  const contractPriv = decodeUriSegment(cs[cs.length - 1]); // FIXME: validate private key

  if (!opts.ignoreComponentError && !contractPriv) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  return Result.of({
    type: TalerUriAction.PayPush,
    exchangeBaseUrl: exchange ?? (cs[0] as HostPortPath),
    contractPriv,
  });
}

function parsePayPull(
  scheme: "http" | "https",
  uriType: TalerUriAction.PayPull,
  cs: string[],
  _params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 2) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get exchange host
  const exchange = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !exchange) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: exchange,
    });
  }
  // get contract priv
  const contractPriv = decodeUriSegment(cs[cs.length - 1]); // FIXME: validate private key
  if (!opts.ignoreComponentError && !contractPriv) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  return Result.of({
    type: TalerUriAction.PayPull,
    exchangeBaseUrl: exchange ?? (cs[0] as HostPortPath),
    contractPriv,
  });
}

function parseRefund(
  scheme: "http" | "https",
  uriType: TalerUriAction.Refund,
  cs: string[],
  _params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 3) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }
  if (cs[cs.length - 1]) {
    // last must be empty
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 1 as const,
      uriType,
    });
  }

  // get merchant host
  const merchant = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -2).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !merchant) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: merchant,
    });
  }

  // get order id
  const orderId = decodeUriSegment(cs[cs.length - 2]);
  return Result.of({
    type: TalerUriAction.Refund,
    merchantBaseUrl: merchant ?? (cs[0] as HostPortPath),
    orderId,
  });
}

function parsePayTemplate(
  scheme: "http" | "https",
  uriType: TalerUriAction.PayTemplate,
  cs: string[],
  params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 2) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get merchant host
  const merchant = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !merchant) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: merchant,
    });
  }
  const templateId = decodeUriSegment(cs[cs.length - 1]);

  const amountParam = params["amount"];
  let amount: AmountString | undefined;
  if (amountParam != undefined && Amounts.checkString(amountParam)) {
    amount = amountParam;
  }

  let nfc: boolean | undefined;
  if ("nfc" in params) {
    nfc = params["nfc"] === "1";
  }

  return Result.of({
    type: TalerUriAction.PayTemplate,
    merchantBaseUrl: merchant ?? (cs[0] as HostPortPath),
    templateId,
    fulfillmentUrl: params["fulfillment_url"],
    sessionId: params["session_id"],
    amount,
    editableAmount: truthyArg(params["editable_amount"]),
    editableSummary: truthyArg(params["editable_summary"]),
    summary: params["summary"],
    nfc,
  });
}

function truthyArg(x: string | undefined): boolean | undefined {
  if (x == null) {
    return undefined;
  }
  if (x === "1") {
    return true;
  }
  return false;
}

function parseRestore(
  scheme: "http" | "https",
  uriType: TalerUriAction.Restore,
  cs: string[],
  _params: Record<string, string>,
  _opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length !== 2) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  const walletPriv = decodeUriSegment(cs[0]); // FIXME: validate private key
  const providers: Array<HostPortPath> = [];
  // const providers = new Array<HostPortPath>();
  for (const name of cs[1].split(",")) {
    let url: string;
    try {
      url = decodeURIComponent(name);
    } catch {
      // Malformed percent-encoding must yield a parse error, not a URIError.
      return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
        uriType: TalerUriAction.Restore as const,
      });
    }

    const isHttp = url.startsWith("http://");
    const withoutScheme = url.startsWith("https://")
      ? url.substring(8)
      : isHttp
        ? url.substring(7)
        : url;

    // Check resolution of this issue https://bugs.gnunet.org/view.php?id=10466
    const thisScheme =
      url === withoutScheme ? scheme : isHttp ? "http" : "https";

    const slashIdx = withoutScheme.indexOf("/");
    const hostname =
      slashIdx < 0 ? withoutScheme : withoutScheme.slice(0, slashIdx);
    const path = slashIdx < 0 ? "" : withoutScheme.slice(slashIdx + 1);
    const host = Paytos.parseHostPortPath2(hostname, path, thisScheme);
    if (host === undefined) {
      return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
        uriType: TalerUriAction.Restore as const,
      });
    }
    providers.push(host);
  }

  return Result.of({
    type: TalerUriAction.Restore,
    providers,
    walletRootPriv: walletPriv ?? (cs[0] as HostPortPath),
  });
}

function parseDevExperiment(
  _scheme: "http" | "https",
  uriType: TalerUriAction.DevExperiment,
  cs: string[],
  params: Record<string, string>,
  _opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length !== 1) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  const devExperimentId = decodeUriSegment(cs[0]);

  return Result.of({
    type: TalerUriAction.DevExperiment,
    devExperimentId,
    query: params,
  });
}

function parseWithdrawExchange(
  scheme: "http" | "https",
  uriType: TalerUriAction.WithdrawExchange,
  cs: string[],
  params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 1) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // FIXME: https://bugs.gnunet.org/view.php?id=10466
  // if (cs[cs.length-1]) {
  if (cs.length > 1 && cs[cs.length - 1]) {
    // last must be empty
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 1 as const,
      uriType,
    });
  }

  // get exchange host
  const exchange = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1, -1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !exchange) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: exchange,
    });
  }

  // get amount param
  const amountRes = !params["a"]
    ? undefined
    : Amounts.parseWithError(params["a"]);
  if (!opts.ignoreComponentError && amountRes && Result.isError(amountRes)) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_PARAMETER, {
      name: "a" as const,
      uriType,
      error: amountRes,
    });
  }
  const amount =
    amountRes && Result.isOk(amountRes)
      ? Amounts.stringify(amountRes.value)
      : undefined;

  return Result.of({
    type: TalerUriAction.WithdrawExchange,
    exchangeBaseUrl: exchange ?? (cs[0] as HostPortPath),
    amount,
  });
}

function parseWithdrawalTransferResult(
  _scheme: "http" | "https",
  uriType: TalerUriAction.WithdrawalTransferResult,
  _cs: string[],
  params: Record<string, string>,
  _opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  const ref = params["ref"];
  if (!ref) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_PARAMETER, {
      uriType,
      name: "ref",
    });
  }
  const status =
    params["status"] !== "aborted" && params["status"] !== "success"
      ? undefined
      : params["status"];
  return Result.of({
    type: TalerUriAction.WithdrawalTransferResult,
    ref,
    status,
  });
}

function parseAddExchange(
  scheme: "http" | "https",
  uriType: TalerUriAction.AddExchange,
  cs: string[],
  params: Record<string, string>,
  opts: TalerUris.PaytoParseOptions = {},
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length === 1) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  // get exchange host
  const exchange = Paytos.parseHostPortPath2(
    cs[0],
    cs.slice(1).join("/"),
    scheme,
  );
  if (!opts.ignoreComponentError && !exchange) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: exchange,
    });
  }

  return Result.of({
    type: TalerUriAction.AddExchange,
    exchangeBaseUrl: exchange ?? (cs[0] as HostPortPath),
  });
}

function parseAddContact(
  scheme: "http" | "https",
  uriType: TalerUriAction.AddContact,
  cs: string[],
  params: Record<string, string>,
): TalerUris.ParseResult {
  // check number of segments
  if (cs.length < 4) {
    return Result.errorWithDetail(TalerUriParseError.COMPONENTS_LENGTH, {
      uriType,
    });
  }

  const aliasType = decodeUriSegment(cs[0]);
  const alias = decodeUriSegment(cs[1]);

  const mailboxBaseUri = Paytos.parseHostPortPath2(
    cs[2],
    cs.slice(3, cs.length - 1).join("/"),
    scheme,
  );
  if (!mailboxBaseUri) {
    return Result.errorWithDetail(TalerUriParseError.INVALID_TARGET_PATH, {
      pos: 0 as const,
      uriType,
      error: mailboxBaseUri,
    });
  }
  const mailboxIdentity = decodeUriSegment(cs[cs.length - 1]);

  return Result.of({
    type: TalerUriAction.AddContact,
    aliasType,
    alias,
    mailboxIdentity,
    mailboxBaseUri,
    sourceBaseUrl: params["sourceBaseUrl"],
  });
}

export interface TalerPayUriResult {
  type: TalerUriAction.Pay;
  merchantBaseUrl: HostPortPath;
  orderId: string;
  /**
   * Session the payment is bound to.  Undefined if the payment is not
   * bound to any session, which the URI encodes as an empty last path
   * component.
   */
  sessionId?: string;
  claimToken?: string;
  /**
   * Nonce priv, only present in the
   * "continue on mobile" payment flow.
   */
  noncePriv?: string;
  nfc?: boolean;
}

export type TemplateParams = {
  amount?: AmountString;
  summary?: string;
};

export interface TalerPayTemplateUri {
  type: TalerUriAction.PayTemplate;
  merchantBaseUrl: HostPortPath;
  templateId: string;
  sessionId?: string;
  fulfillmentUrl?: string;
  editableSummary?: boolean;
  summary?: string;
  editableAmount?: boolean;
  amount?: AmountString;
  nfc?: boolean;
}

export interface TalerWithdrawUri {
  type: TalerUriAction.Withdraw;
  bankIntegrationApiBaseUrl: HostPortPath;
  withdrawalOperationId: string;
  externalConfirmation?: boolean;
}

export interface TalerRefundUri {
  type: TalerUriAction.Refund;
  merchantBaseUrl: HostPortPath;
  orderId: string;
}

export interface TalerPayPushUri {
  type: TalerUriAction.PayPush;
  exchangeBaseUrl: HostPortPath;
  contractPriv: string;
}

export interface TalerPayPullUri {
  type: TalerUriAction.PayPull;
  exchangeBaseUrl: HostPortPath;
  contractPriv: string;
}

export interface TalerDevExperimentUri {
  type: TalerUriAction.DevExperiment;
  devExperimentId: string;
  query?: Record<string, string>;
}

export interface TalerRestoreUri {
  type: TalerUriAction.Restore;
  walletRootPriv: string;
  providers: Array<HostPortPath>;
}

export interface TalerWithdrawExchangeUri {
  type: TalerUriAction.WithdrawExchange;
  exchangeBaseUrl: HostPortPath;
  amount?: AmountString;
}

export interface TalerAddExchangeUri {
  type: TalerUriAction.AddExchange;
  exchangeBaseUrl: HostPortPath;
}
export interface TalerWithdrawalTransferResultUri {
  type: TalerUriAction.WithdrawalTransferResult;
  ref: string;
  status?: "success" | "aborted";
}

export interface TalerAddContactUri {
  type: TalerUriAction.AddContact;
  alias: string;
  aliasType: string;
  mailboxBaseUri: string;
  mailboxIdentity: HashCodeString;
  sourceBaseUrl: string;
}

/**
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_rfc3986
 */
function encodeRFC3986URIComponent(str: string): string {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}
const rfc3986 = encodeRFC3986URIComponent;

/**
 * Encode a value as a single taler:// URI path segment.
 *
 * "." and ".." are resolved away by the URL parser, even when percent-encoded,
 * so a component equal to either cannot be represented and is rejected rather
 * than silently erasing the preceding path.
 */
function encodeUriSegment(s: string): string {
  if (s === "." || s === "..") {
    throw Error(`cannot represent taler:// path segment ${JSON.stringify(s)}`);
  }
  // Only the characters that would break the positional/query parsing are
  // escaped, so that components without them keep their on-the-wire form.
  // Tab, LF and CR are among them because the URL parser removes them before
  // parsing, which would otherwise turn "..\t" back into a dot segment.
  return s.replace(
    /[%/?#\t\n\r]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`,
  );
}

/**
 * Decode a single taler:// URI path segment.  Falls back to the raw value for
 * input that is not valid percent-encoding, so parsing never throws on it.
 */
function decodeUriSegment(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 *
 * https://www.rfc-editor.org/rfc/rfc3986
 */
function createSearchParams(paramList: [string, string][]): string {
  return paramList
    .map(([key, value]) => `${rfc3986(key)}=${rfc3986(value)}`)
    .join("&");
}
